<!--
// app/pages/[region]/stats/Passing.vue
-->
<script setup lang="ts">
import type { GroupedRanking } from "~/src/stats/domain/models/Rankings";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import statsService from "~/src/stats/domain/services/statsService";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import StatsDataTable from "~/app/components/pages/region-stats/StatsDataTable.vue";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import ButtonSelectTypeStats from "~/app/components/pages/region-stats/ButtonSelectTypeStats.vue";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats/passing",
    es: "/[region]/estadisticas/pases",
    de: "/[region]/statistiken/passe",
    fr: "/[region]/statistiques/passes",
    it: "/[region]/statistiche/passaggi",
    pt: "/[region]/estatisticas/passes",
    ar: "/[region]/stats/passing",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { mergeRankingsByPlayerId } = statsService();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { webContextGenders } = useWebContext();
const { t } = useI18n();
const router = useRouter();
const localePath = useLocalePath();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);

const errorMessage = ref<boolean>(false);
const tableDataPlayers = ref<GroupedRanking[] | undefined>([]);

const mainStat = ref<string>("ASS-V");
const otherStats = ref<string>("PSP-X,PAS-CR,PG");
const allStatsPlayer: string[] = ["ASS-V", "PAS", "PAS-X", "CRS", "PAS-CR", "PG"];
const fallbackMainStat: string = "ASS-V";

const getSeasonData = async (): Promise<void> => {
  if (!$SEASONS_WITHOUT_DATA.includes(Number(seasonId.value)) || !seasonId.value) {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;

      const seasonIdValue = seasonId.value;

      const { data } = await useFetch("/api/stats", {
        query: {
          season: seasonIdValue,
          type: "players",
          mainCode: mainStat.value,
          secondaryCodes: otherStats.value,
          orderBy: "ranking",
          sortOrder: "ASC",
          ctxGenders: webContextGenders,
        },
      });
      tableDataPlayers.value = data.value || [];
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      tableStatsKey.value++;
      sidebarKey.value++;
    }
  } else {
    await nextTick();
    await router.push(
      localePath({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title: t("menuItem.stats") + " " + t("menuItem.statsPlayers") + " - " + t("stats.sidebar.menu.Passing"),
  ogTitle: t("menuItem.stats") + " " + t("menuItem.statsPlayers") + " - " + t("stats.sidebar.menu.Passing"),
});

const { sortStatsTable } = statsService();

const executeSortPlayers = async sortValue => {
  const codeSortValue = sortValue.sortField.split(".")[1];
  const sortOrderValue = sortValue.sortOrder;

  mainStat.value = codeSortValue;
  tableDataPlayers.value = await sortStatsTable(
    codeSortValue,
    allStatsPlayer,
    fallbackMainStat,
    seasonId.value,
    "players",
    sortOrderValue
  );
};

const dataFieldsPlayers = computed(() => {
  return {
    ranking: `rankingData.${mainStat.value}.ranking`,
    name: "player",
    position: "player.role",
    sortField: "rankingData.ASS-V.total",
    stats: [
      { stat: "rankingData.PG.total", header: t("PJ"), sortable: false, tooltip: t("tooltip.PJ") },
      { stat: "rankingData.ASS-V.total", header: t("Asist"), sortable: true, tooltip: t("tooltip.Asist") },
      { stat: "rankingData.PAS-CR.total", header: t("KeyP"), sortable: true, tooltip: t("tooltip.KeyP") },
    ],
  };
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('menuItem.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div
      class="kql-w-container flex flex-col md:flex-row items-stretch sm:items-end justify-center md:items-center md:justify-between flex-wrap"
    >
      <button-select-type-stats
        v-if="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
        data-type="players"
        page="Passing"
      ></button-select-type-stats>
      <div v-else></div>
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
        combined-with-more-filters
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div v-else class="stats-table-data max-w-full overflow-hidden" :key="tableStatsKey">
          <stats-data-table
            :main-stat="mainStat"
            :table-data="tableDataPlayers"
            :data-fields="dataFieldsPlayers"
            @sort="e => executeSortPlayers(e)"
          ></stats-data-table>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
